import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { API_BASE } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import DeleteConfirmModal from '../../components/admin/DeleteConfirmModal';
import ArtworkForm from '../../components/admin/ArtworkForm';
import { ArtReproduction, Tag, Size, ArtistProfile } from '../../types';
import { getArtworkDisplayUrl, useViewportWidth } from '../../hooks/useArtworkDisplayUrl';
import { validateFile } from '../../constants/fileUpload';
import './AdminArtworks.css';

interface AspectRatio {
  id: number;
  name: string;
  ratio?: number;
  defaultSizes?: Array<{ id: number; sizeId?: number; size?: Size; effectivePrice?: number }>;
  additionalSizes?: Array<{ id: number; sizeId?: number; size?: Size; effectivePrice?: number }>;
}

interface SizePriceOverride {
  size: string;
  width: string;
  height: string;
  price: string;
  isOriginal: boolean;
}

interface ArtworkFormData {
  title: string;
  artistName: string;
  artistId: string;
  description: string;
  price: string;
  imageUrl: string;
  aspectRatioId: string;
  hasOriginal: boolean;
  isOriginalAvailable: boolean;
  originalSize: string;
  originalWidth: string;
  originalHeight: string;
  originalPrice: string;
  tagIds: number[];
  sizePrices: SizePriceOverride[];
  imageFile?: File;
}

interface ExtendedArtReproduction extends ArtReproduction {
  aspectRatioId?: number;
  hasOriginal?: boolean;
  originalSize?: string;
  originalWidth?: number;
  originalHeight?: number;
  sizePrices?: Array<{
    size: string;
    width?: number;
    height?: number;
    price: number;
    isOriginal?: boolean;
  }>;
  tags?: Tag[];
}

const AdminArtworks: React.FC = () => {
  const { success, error: showError } = useToast();
  const { user } = useAuth();
  const isArtist = user?.role === 'Artist';
  const artApiBase = isArtist ? '/artist/art' : '/admin/art';
  const [artworks, setArtworks] = useState<ExtendedArtReproduction[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [aspectRatios, setAspectRatios] = useState<AspectRatio[]>([]);
  const [allSizes, setAllSizes] = useState<Size[]>([]);
  const [artistOptions, setArtistOptions] = useState<ArtistProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingArtwork, setEditingArtwork] = useState<ExtendedArtReproduction | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; id: number | null; name?: string }>({ show: false, id: null });
  const [deleting, setDeleting] = useState<boolean>(false);
  const [formData, setFormData] = useState<ArtworkFormData>({
    title: '',
    artistName: '',
    artistId: '',
    description: '',
    price: '',
    imageUrl: '',
    aspectRatioId: '',
    hasOriginal: false,
    isOriginalAvailable: false,
    originalSize: '',
    originalWidth: '',
    originalHeight: '',
    originalPrice: '',
    tagIds: [],
    sizePrices: []
  });
  const [uploading, setUploading] = useState<boolean>(false);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio | null>(null);
  const viewportWidth = useViewportWidth();
  const [originalSizeId, setOriginalSizeId] = useState<string>('');

  const fetchAllSizes = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Size[]>('/sizes');
      setAllSizes(response.data);
    } catch (error) {
      console.error('Failed to fetch sizes:', error);
    }
  }, []);

  const fetchArtists = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<ArtistProfile[]>('/admin/artists');
      setArtistOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  }, []);


  // Auto-detect aspect ratio when size is entered
  useEffect(() => {
    if (formData.originalWidth && formData.originalHeight) {
      const detectAspectRatio = async (): Promise<void> => {
        try {
          const width = parseFloat(formData.originalWidth);
          const height = parseFloat(formData.originalHeight);
          
          if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
            return;
          }
          
          const response = await api.post<{ aspectRatioId?: number }>(`${artApiBase}/detect-aspect-ratio`, {
            width: width,
            height: height
          });
          
          if (response.data.aspectRatioId) {
            const detectedAspectRatioId = response.data.aspectRatioId.toString();
            // Only update if aspect ratio changed or wasn't set
            if (formData.aspectRatioId !== detectedAspectRatioId) {
              setFormData(prev => ({ ...prev, aspectRatioId: detectedAspectRatioId }));
            }
          }
        } catch (error) {
          console.error('Failed to detect aspect ratio:', error);
        }
      };
      
      // Debounce the detection to avoid too many API calls
      const timeoutId = setTimeout(detectAspectRatio, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [artApiBase, formData.originalWidth, formData.originalHeight, formData.aspectRatioId]);

  useEffect(() => {
    if (formData.aspectRatioId) {
      const ar = aspectRatios.find(a => a.id === parseInt(formData.aspectRatioId));
      if (ar) {
        setSelectedAspectRatio(ar);
        // If original size is set, try to find matching size ID
        if (formData.originalSize && formData.originalWidth && formData.originalHeight) {
          const allSizes = [...(ar.defaultSizes || []), ...(ar.additionalSizes || [])];
          const matchingSize = allSizes.find(spd => 
            spd && spd.size && 
            spd.size.width === parseFloat(formData.originalWidth) && 
            spd.size.height === parseFloat(formData.originalHeight)
          );
          if (matchingSize && matchingSize.size) {
            setOriginalSizeId(matchingSize.size.id.toString());
          }
        }
      } else {
        setSelectedAspectRatio(null);
      }
    } else {
      setSelectedAspectRatio(null);
      setOriginalSizeId('');
    }
  }, [formData.aspectRatioId, aspectRatios, formData.originalSize, formData.originalWidth, formData.originalHeight]);

  const fetchArtworks = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<ExtendedArtReproduction[]>(artApiBase);
      setArtworks(response.data);
    } catch (error) {
      console.error('Failed to fetch artworks:', error);
    } finally {
      setLoading(false);
    }
  }, [artApiBase]);

  const fetchTags = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Tag[]>('/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  }, []);

  const fetchAspectRatios = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<AspectRatio[]>('/admin/aspect-ratios');
      setAspectRatios(response.data);
    } catch (error) {
      console.error('Failed to fetch aspect ratios:', error);
    }
  }, []);

  useEffect(() => {
    fetchArtworks();
    fetchTags();
    fetchAspectRatios();
    fetchAllSizes();
    if (!isArtist) {
      fetchArtists();
    } else {
      // Auto-set artistId for artists
      if (user?.id) {
        setFormData(prev => ({ ...prev, artistId: user.id.toString() }));
      }
    }
  }, [fetchAllSizes, fetchArtists, fetchArtworks, fetchAspectRatios, fetchTags, isArtist, user?.id]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file using centralized validation
    const validation = validateFile(file);
    if (!validation.isValid) {
      showError(validation.error || 'Invalid file');
      e.target.value = ''; // Clear the input
      return;
    }

    setUploading(true);
    try {
      // Store file for form submission
      setFormData(prev => ({ ...prev, imageFile: file }));
    } catch (error) {
      showError('Failed to select image');
    } finally {
      setUploading(false);
    }
  };

  const handleTagToggle = (tagId: number): void => {
    setFormData(prev => ({
      ...prev,
      tagIds: prev.tagIds.includes(tagId)
        ? prev.tagIds.filter(id => id !== tagId)
        : [...prev.tagIds, tagId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      if (!formData.title.trim()) {
        showError('Title is required.');
        return;
      }
      if (!isArtist && !formData.artistId) {
        showError('Please select an artist.');
        return;
      }

      const submitData = new FormData();
      submitData.append('Title', formData.title);
      // Only send ArtistId, backend will derive ArtistName
      if (formData.artistId) {
        submitData.append('ArtistId', formData.artistId);
      } else if (isArtist && user?.id) {
        // For artists, ensure their ID is set
        submitData.append('ArtistId', user.id.toString());
      }
      submitData.append('Description', formData.description || '');
      submitData.append('Price', parseFloat(formData.price).toString() || '0');
      if (formData.aspectRatioId) {
        submitData.append('AspectRatioId', formData.aspectRatioId);
      }
      // hasOriginal is true if there's a size selected (represents max size, which could be an original)
      // isOriginalAvailable determines if the original piece is actually available for purchase
      const hasOriginal = !!(formData.originalSize && formData.originalWidth && formData.originalHeight);
      // Ensure isOriginalAvailable is always a boolean
      const isOriginalAvailable = Boolean(formData.isOriginalAvailable);
      submitData.append('HasOriginal', hasOriginal.toString());
      submitData.append('IsOriginalAvailable', isOriginalAvailable.toString());
      if (formData.originalSize) {
        submitData.append('OriginalSize', formData.originalSize);
        if (formData.originalWidth) submitData.append('OriginalWidth', formData.originalWidth);
        if (formData.originalHeight) submitData.append('OriginalHeight', formData.originalHeight);
        if (isOriginalAvailable && formData.originalPrice) {
          submitData.append('OriginalPrice', formData.originalPrice);
        }
      }
      submitData.append('IsActive', 'true');
      if (formData.tagIds && formData.tagIds.length > 0) {
        formData.tagIds.forEach(id => submitData.append('TagIds', id.toString()));
      }
      // Image is required - must have either ImageFile or ImageUrl
      if (formData.imageFile) {
        submitData.append('ImageFile', formData.imageFile);
      } else if (formData.imageUrl && formData.imageUrl.trim()) {
        submitData.append('ImageUrl', formData.imageUrl.trim());
      } else {
        showError('Please upload an image file');
        return;
      }
      // Only send SizePrices if there are any
      if (formData.sizePrices && formData.sizePrices.length > 0) {
        formData.sizePrices.forEach((sp, index) => {
          if (sp.size && sp.price) {
            submitData.append(`SizePrices[${index}].Size`, sp.size);
            if (sp.width) submitData.append(`SizePrices[${index}].Width`, sp.width);
            if (sp.height) submitData.append(`SizePrices[${index}].Height`, sp.height);
            submitData.append(`SizePrices[${index}].Price`, sp.price);
            submitData.append(`SizePrices[${index}].IsOriginal`, (sp.isOriginal || false).toString());
          }
        });
      }

      setSaving(true);
      if (editingArtwork) {
        await api.put(`${artApiBase}/${editingArtwork.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        success('Artwork updated');
      } else {
        await api.post(artApiBase, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        success('Artwork created');
      }

      setShowForm(false);
      setEditingArtwork(null);
      resetForm();
      fetchArtworks();
    } catch (error: any) {
      console.error('Failed to save artwork:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to save artwork';
      if (error.response?.data) {
        // Check for ModelState errors (ASP.NET validation)
        if (error.response.data.errors) {
          const errors = Object.entries(error.response.data.errors)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMessage = `Validation errors:\n${errors}`;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showError(`Failed to save artwork: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (artwork: ExtendedArtReproduction): void => {
    setEditingArtwork(artwork);
    setFormData({
      title: artwork.title || '',
      artistName: '', // Not used anymore, backend derives it
      artistId: artwork.artistId ? artwork.artistId.toString() : (isArtist && user?.id ? user.id.toString() : ''),
      description: artwork.description || '',
      price: artwork.price?.toString() || '',
      imageUrl: artwork.imageUrl || '',
      aspectRatioId: artwork.aspectRatioId ? artwork.aspectRatioId.toString() : '',
      hasOriginal: artwork.hasOriginal || false,
      isOriginalAvailable: artwork.isOriginalAvailable !== undefined ? artwork.isOriginalAvailable : false,
      originalSize: artwork.originalSize || '',
      originalWidth: artwork.originalWidth ? artwork.originalWidth.toString() : '',
      originalHeight: artwork.originalHeight ? artwork.originalHeight.toString() : '',
      originalPrice: artwork.sizePrices?.find(sp => sp.isOriginal)?.price?.toString() || '',
      tagIds: artwork.tags ? artwork.tags.map(t => t.id) : [],
      sizePrices: artwork.sizePrices?.filter(sp => !sp.isOriginal).map(sp => ({
        size: sp.size,
        width: sp.width?.toString() || '',
        height: sp.height?.toString() || '',
        price: sp.price.toString(),
        isOriginal: false
      })) || []
    });
    // Set original size ID if aspect ratio and original dimensions are available
    if (artwork.aspectRatioId && artwork.originalWidth && artwork.originalHeight) {
      const ar = aspectRatios.find(a => a.id === artwork.aspectRatioId);
      if (ar) {
        const allSizes = [...(ar.defaultSizes || []), ...(ar.additionalSizes || [])];
        const matchingSize = allSizes.find(spd => 
          spd.size && 
          spd.size.width === artwork.originalWidth && 
          spd.size.height === artwork.originalHeight
        );
        if (matchingSize && matchingSize.size) {
          setOriginalSizeId(matchingSize.size.id.toString());
        } else {
          setOriginalSizeId('');
        }
      } else {
        setOriginalSizeId('');
      }
    } else {
      setOriginalSizeId('');
    }
    setShowForm(true);
  };

  const handleDelete = (artworkId: number): void => {
    setDeleteConfirm({ show: true, id: artworkId, name: artworks.find(a => a.id === artworkId)?.title });
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteConfirm.id) return;
    try {
      setDeleting(true);
      await api.delete(`${artApiBase}/${deleteConfirm.id}`);
      success('Artwork deleted');
      fetchArtworks();
      setDeleteConfirm({ show: false, id: null });
    } catch (error) {
      showError('Failed to delete artwork');
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePin = async (artworkId: number): Promise<void> => {
    try {
      const response = await api.patch<{ isPinned: boolean }>(`${artApiBase}/${artworkId}/pin`);
      success(response.data.isPinned ? 'Artwork pinned' : 'Artwork unpinned');
      fetchArtworks();
    } catch (error) {
      showError('Failed to toggle pin');
    }
  };

  const resetForm = (): void => {
    setFormData({
      title: '',
      artistName: '', // Not used anymore, backend derives it
      artistId: isArtist && user?.id ? user.id.toString() : '',
      description: '',
      price: '',
      imageUrl: '',
      aspectRatioId: '',
      hasOriginal: false,
      isOriginalAvailable: false,
      originalSize: '',
      originalWidth: '',
      originalHeight: '',
      originalPrice: '',
      tagIds: [],
      sizePrices: []
    });
    setSelectedAspectRatio(null);
    setOriginalSizeId('');
  };

  const addSizePriceOverride = (): void => {
    setFormData({
      ...formData,
      sizePrices: [...formData.sizePrices, { size: '', width: '', height: '', price: '', isOriginal: false }]
    });
  };

  const removeSizePriceOverride = (index: number): void => {
    setFormData({
      ...formData,
      sizePrices: formData.sizePrices.filter((_, i) => i !== index)
    });
  };

  const updateSizePriceOverride = (index: number, field: keyof SizePriceOverride, value: string): void => {
    const updated = [...formData.sizePrices];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, sizePrices: updated });
  };

  const handleCancel = (): void => {
    setShowForm(false);
    setEditingArtwork(null);
    resetForm();
  };

  const handleOriginalSizeIdChange = (sizeId: string): void => {
    setOriginalSizeId(sizeId);
    const selectedSize = allSizes.find(s => s.id === parseInt(sizeId));
    if (selectedSize) {
      setFormData({
        ...formData,
        originalSize: selectedSize.name,
        originalWidth: selectedSize.width.toString(),
        originalHeight: selectedSize.height.toString()
      });
    }
  };

  if (loading) {
    return <div className="loading">Loading artworks...</div>;
  }

  return (
    <div className="admin-artworks">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1 className="page-title">Artwork Management</h1>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingArtwork(null);
              setShowForm(true);
            }}
            className="btn btn-primary"
          >
            <i className="fas fa-plus"></i> Add Artwork
          </button>
        </div>

        {showForm && (
          <div className="artwork-form-container">
            <form onSubmit={handleSubmit}>
              <h2>{editingArtwork ? 'Edit Artwork' : 'Create Artwork'}</h2>
              <ArtworkForm
                formData={formData}
                tags={tags}
                allSizes={allSizes}
                artistOptions={artistOptions}
                isArtist={isArtist}
                selectedAspectRatio={selectedAspectRatio}
                originalSizeId={originalSizeId}
                uploading={uploading}
                onFormDataChange={(data) => setFormData({ ...formData, ...data })}
                onOriginalSizeIdChange={handleOriginalSizeIdChange}
                onFileUpload={handleFileUpload}
                onTagToggle={handleTagToggle}
                onAddSizePriceOverride={addSizePriceOverride}
                onRemoveSizePriceOverride={removeSizePriceOverride}
                onUpdateSizePriceOverride={updateSizePriceOverride}
              />
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={uploading || saving}>
                  {saving && <span className="btn-spinner" aria-hidden="true"></span>}
                  {saving ? 'Saving...' : (editingArtwork ? 'Update' : 'Create')}
                </button>
                <button type="button" onClick={handleCancel} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <DeleteConfirmModal
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, id: null })}
          onConfirm={confirmDelete}
          loading={deleting}
          itemName={deleteConfirm.name}
        />

        <div className="artworks-grid">
          {artworks.map((artwork) => (
            <div key={artwork.id} className="artwork-card">
              <div className="artwork-image">
                <img
                  src={((): string => {
                    const url = getArtworkDisplayUrl(artwork, viewportWidth);
                    return url.startsWith('http') ? url : `${API_BASE}${url}`;
                  })()}
                  alt={artwork.title}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const fallback = (artwork.thumbnailUrl || artwork.imageUrl).startsWith('http')
                      ? (artwork.thumbnailUrl || artwork.imageUrl)
                      : `${API_BASE}${artwork.thumbnailUrl || artwork.imageUrl}`;
                    if (target.src !== fallback) target.src = fallback;
                    else target.src = '/placeholder-image.jpg';
                  }}
                />
              </div>
              <div className="artwork-info">
                <h3>{artwork.title}</h3>
                {artwork.artistName && <p className="artist-name">by {artwork.artistName}</p>}
                <p className="artwork-price">${artwork.price.toFixed(2)}</p>
                {artwork.tags && artwork.tags.length > 0 && (
                  <div className="artwork-tags">
                    {artwork.tags.map(tag => (
                      <span key={tag.id} className="tag-badge">{tag.name}</span>
                    ))}
                  </div>
                )}
                <div className="artwork-actions">
                  <button
                    onClick={() => handleTogglePin(artwork.id)}
                    className={`btn btn-small ${artwork.isPinned ? 'btn-warning' : 'btn-secondary'}`}
                    title={artwork.isPinned ? 'Unpin artwork' : 'Pin artwork'}
                  >
                    <i className={`fas fa-thumbtack ${artwork.isPinned ? '' : 'fa-rotate-90'}`}></i> {artwork.isPinned ? 'Pinned' : 'Pin'}
                  </button>
                  <button
                    onClick={() => handleEdit(artwork)}
                    className="btn btn-primary btn-small"
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(artwork.id)}
                    className="btn btn-danger btn-small"
                  >
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminArtworks;
